import {
  THEME_STORAGE_KEY,
  applyTheme,
  parseThemePreference,
  readThemePreference,
  storeThemePreference,
  watchSystemTheme,
} from '@didto/ui';
import type { ThemePreference } from '@didto/ui';
import { useSyncExternalStore } from 'react';

/* The theme chosen on this device (Settings, Appearance; ADR-042). public/theme-init.js showed it
   before the first paint; from here the app keeps it: the device's own setting while the choice is
   "system", and a choice made in another tab of the same app. */
let preference: ThemePreference = readThemePreference();
const listeners = new Set<() => void>();

function emit() {
  for (const l of listeners) l();
}

export function setThemePreference(pref: ThemePreference): void {
  preference = pref;
  storeThemePreference(pref);
  applyTheme(pref);
  emit();
}

/** Starts following the device and other tabs; returns how to stop (for tests). */
export function startTheme(): () => void {
  applyTheme(preference);
  const stopSystem = watchSystemTheme(() => {
    if (preference === 'system') applyTheme('system');
  });
  const onStorage = (e: StorageEvent) => {
    if (e.key !== THEME_STORAGE_KEY) return;
    preference = parseThemePreference(e.newValue);
    applyTheme(preference);
    emit();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    stopSystem();
    window.removeEventListener('storage', onStorage);
  };
}

export const THEME_LABEL: Record<ThemePreference, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
};
/** The account card's quick switch steps through the three in this order. */
export const NEXT_THEME: Record<ThemePreference, ThemePreference> = {
  system: 'light',
  light: 'dark',
  dark: 'system',
};

export function useThemePreference(): ThemePreference {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => preference,
  );
}
